import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { auth, db } from '../services/firebase';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { doc, getDoc, getDocFromServer, getDocsFromServer, query, collection, where, getDocs, writeBatch, updateDoc, onSnapshot, deleteField } from 'firebase/firestore';
import { useToast } from './ToastContext';
import { logger } from '../utils/logger';
import { logAudit } from '../utils/audit';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    role: 'psicologo' | 'paciente' | 'apoio' | 'paciente_inativo' | 'psicologo_inativo' | 'apoio_inativo' | 'master' | 'admin' | 'support' | null;
    psychologistId: string | null; // ID do psicólogo responsável (ou o próprio ID se for psicólogo)
    permissions: string[]; // ['patients', 'schedule', 'financial'] etc.
    userName: string | null;
    photoURL: string | null; // Foto do perfil do usuário
    isPlanExpired: boolean;
    psychData: any | null;
    logout: () => Promise<void>;
    refreshRole: () => Promise<void>;
    isFirstAccess: boolean; // Controla se é o primeiro acesso (para troca de senha)
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

// ─── Role Cache (localStorage) ───────────────────────────────────────────────
// Persists user role across PWA restarts independently of Firestore cache.
// This is the key fix for patient sessions: Firestore doc-by-UID works for
// psicólogos (doc ID = UID) but not reliably for patients (legacy doc IDs).
const ROLE_CACHE_KEY = 'cognitya_session_v2';
const ROLE_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

interface RoleCache {
    uid: string;
    role: string;
    psychologistId: string | null;
    userName: string | null;
    photoURL: string | null;
    permissions: string[];
    isFirstAccess: boolean;
    ts: number;
}

const saveRoleCache = (data: RoleCache) => {
    try { localStorage.setItem(ROLE_CACHE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
};

const loadRoleCache = (uid: string): RoleCache | null => {
    try {
        const raw = localStorage.getItem(ROLE_CACHE_KEY);
        if (!raw) return null;
        const cache: RoleCache = JSON.parse(raw);
        if (cache.uid !== uid) return null; // Different user
        if (Date.now() - cache.ts > ROLE_CACHE_TTL) return null; // Expired
        return cache;
    } catch { return null; }
};

const clearRoleCache = () => {
    try { localStorage.removeItem(ROLE_CACHE_KEY); } catch { /* ignore */ }
};

// Reusable helper to get a document with a timeout and server-first/cache-fallback behavior
const getDocWithTimeout = async (docRef: any, timeoutMs = 2500) => {
    try {
        const serverSnap = await Promise.race([
            getDocFromServer(docRef),
            new Promise<never>((_, reject) => 
                setTimeout(() => reject(new Error('Timeout fetching from server')), timeoutMs)
            )
        ]);
        return serverSnap;
    } catch (err) {
        logger.warn(`[AuthContext] Server fetch failed or timed out for ${docRef.path}, falling back to cache/local:`, err);
        return getDoc(docRef); // Fallback to cache/local SDK resolution
    }
};

// Reusable helper to get docs query with a timeout and server-first/cache-fallback behavior
const getDocsWithTimeout = async (firestoreQuery: any, timeoutMs = 2500) => {
    try {
        const serverSnap = await Promise.race([
            getDocsFromServer(firestoreQuery),
            new Promise<never>((_, reject) => 
                setTimeout(() => reject(new Error('Timeout fetching query from server')), timeoutMs)
            )
        ]);
        return serverSnap;
    } catch (err) {
        logger.warn(`[AuthContext] Server query failed or timed out, falling back to cache/local:`, err);
        return getDocs(firestoreQuery); // Fallback to cache/local SDK resolution
    }
};
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

const checkIsPublicRoute = (hash: string) => {
    const path = hash.replace(/^#/, ''); // Remove # if exists
    const cleanPath = path.split('?')[0];
    return cleanPath === '' || cleanPath === '/' || cleanPath === '/privacy' || cleanPath === '/terms' || cleanPath === '/plans' || cleanPath === '/reset-password';
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isPublicRoute, setIsPublicRoute] = useState(() => checkIsPublicRoute(window.location.hash));
    const [persistenceReady, setPersistenceReady] = useState(false);
    const authInitializedRef = useRef(false);
    const unsubscribeRef = useRef<(() => void) | null>(null);

    // Wait for the Firebase Auth persistence layer to be initialized before checking auth state
    useEffect(() => {
        let active = true;
        const initPersistence = async () => {
            try {
                const { persistenceInitPromise } = await import('../services/firebase');
                await persistenceInitPromise;
            } catch (e) {
                logger.error('[AuthContext] Error awaiting persistence:', e);
            }
            if (active) {
                setPersistenceReady(true);
            }
        };
        initPersistence();
        return () => {
            active = false;
        };
    }, []);

    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState<'psicologo' | 'paciente' | 'apoio' | 'paciente_inativo' | 'psicologo_inativo' | 'apoio_inativo' | 'master' | 'admin' | 'support' | null>(null);
    const [isFirstAccess, setIsFirstAccess] = useState(false);
    const [psychologistId, setPsychologistId] = useState<string | null>(null);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [userName, setUserName] = useState<string | null>(null);
    const [photoURL, setPhotoURL] = useState<string | null>(null);
    const [isPlanExpired, setIsPlanExpired] = useState(false);
    const [psychData, setPsychData] = useState<any | null>(null);
    const { showToast } = useToast();

    // Helper to fetch user role data
    const fetchUserRole = async (uid: string, firebaseUser?: any, retries = 3) => {
        const userEmail = firebaseUser?.email || auth.currentUser?.email || '';
        const isPatientEmail = userEmail.endsWith('@cognitya.patient');
        const isStaffEmail = userEmail.endsWith('@cognitya.staff');
        const isSyntheticEmail = isPatientEmail || isStaffEmail;

        try {
            // Garante que o IndexedDB de cache offline está pronto antes de tentar buscar
            try {
                const { persistenceInitPromise } = await import('../services/firebase');
                await persistenceInitPromise;
            } catch (e) {}

            let currentIsExpired = false;
            let currentPsychData: any = null;

            // 1. Check SysAdmin (Aurora Master/Admin) - PRIORIDADE MÁXIMA
            if (!isSyntheticEmail) {
                const adminRef = doc(db, 'sys_admins', uid);
                const adminSnap = await getDocWithTimeout(adminRef);
                if (adminSnap.exists()) {
                    const adminData = adminSnap.data();
                    if (adminData.active) {
                        setRole(adminData.role as any);
                        setUserName(adminData.name || 'Admin Aurora');
                        setPermissions(['all']);
                        setIsFirstAccess(false);
                        setIsPlanExpired(false);
                        setPsychologistId(null);
                        setPhotoURL(null);
                        setPsychData(null);
                        saveRoleCache({ uid, role: adminData.role as string, psychologistId: null, userName: adminData.name || 'Admin Aurora', photoURL: null, permissions: ['all'], isFirstAccess: false, ts: Date.now() });
                        return;
                    }
                }
            }

            // 2. Check Psicólogo (Document ID == Auth UID)
            // SEGURANÇA: Se for e-mail sintético, PULA check de psicólogo
            if (!isSyntheticEmail) {
                const psychRef = doc(db, 'psicologos', uid);
                const psychSnap = await getDocWithTimeout(psychRef);
                
                if (psychSnap.exists()) {
                    const data = psychSnap.data();
                    currentPsychData = data;
                    const trialEnd = data.trialEndsAt ? (data.trialEndsAt.toDate ? data.trialEndsAt.toDate() : new Date(data.trialEndsAt)) : null;
                    const nextBillingDate = data.nextBillingDate ? (data.nextBillingDate.toDate ? data.nextBillingDate.toDate() : new Date(data.nextBillingDate)) : null;
                    const isBillingExpired = nextBillingDate && new Date() > nextBillingDate && data.plan && data.plan.toLowerCase() !== 'gratuito';
                    const isSubscribed = data.subscriptionStatus === 'active' || data.subscriptionStatus === 'canceled' || data.subscriptionStatus === 'overdue';

                    if (data.status === 'inativo' && data.deactivatedBy !== 'user' && data.deactivatedBy !== 'admin') {
                        currentIsExpired = true;
                    } else if (data.subscriptionStatus === 'expirado' || data.subscriptionStatus === 'overdue' || data.status === 'inadimplente' || isBillingExpired) {
                        currentIsExpired = true;
                    } else if (trialEnd && new Date() > trialEnd && !isSubscribed && data.status !== 'inativo') {
                        currentIsExpired = true;
                    }

                    if (data.status === 'inativo' && data.deactivatedBy === 'user') {
                        setRole('psicologo_inativo');
                    } else if (data.status === 'inativo' && !currentIsExpired) {
                        setRole('psicologo_inativo');
                    } else {
                        setRole('psicologo');
                    }
                    setPsychologistId(uid);
                    setPermissions(['all']);
                    setUserName(data.nome || data.name || 'Doutor(a)');
                    setPhotoURL(data.photoURL || null);
                    setIsFirstAccess(false);
                    setIsPlanExpired(currentIsExpired);
                    setPsychData(currentPsychData);
                    saveRoleCache({ uid, role: currentIsExpired ? 'psicologo' : (data.status === 'inativo' ? 'psicologo_inativo' : 'psicologo'), psychologistId: uid, userName: data.nome || data.name || 'Doutor(a)', photoURL: data.photoURL || null, permissions: ['all'], isFirstAccess: false, ts: Date.now() });
                    return;
                }
            }

            // 2. Check Apoio (Document ID != Auth UID usually, so we query by field 'uid')
            // Se for explicitamente um paciente, pula essa busca para economizar tempo e evitar travamentos
            if (!isPatientEmail) {
                let supportDocRef = doc(db, 'apoio_users', uid);
                let supportSnap = await getDocWithTimeout(supportDocRef);

                if (supportSnap && supportSnap.exists()) {
                    let data = supportSnap.data();

                // --- REATIVAÇÃO SILENCIOSA (Background) ---
                if (data.status === 'inativo' && data.deactivatedBy === 'user') {
                    const scheduledDeletion = data.scheduledDeletionAt ? (data.scheduledDeletionAt.toDate ? data.scheduledDeletionAt.toDate() : new Date(data.scheduledDeletionAt)) : null;
                    if (scheduledDeletion && new Date() < scheduledDeletion) {
                        logger.log(`[AuthContext] ⚡ Reativação silenciosa detectada para Apoio ${uid}`);
                        await updateDoc(supportDocRef, {
                            status: 'ativo',
                            accountStatus: 'active',
                            deactivatedBy: deleteField(),
                            deactivatedAt: deleteField(),
                            scheduledDeletionAt: deleteField()
                        });
                        data.status = 'ativo'; // Atualiza localmente para uso imediato
                    }
                }

                // SELF-REPAIR: Ensure 'role' field exists for Firestore Rules
                if (data.role !== 'apoio') {
                    logger.log("[AuthContext] 🛠️ Self-Repair: Adding missing 'role' field to Apoio User doc...");
                    try {
                        await updateDoc(supportDocRef, { role: 'apoio' });
                        logger.log("[AuthContext] Self-Repair successful.");
                    } catch (e) {
                        logger.warn("[AuthContext] Could not auto-repair role field:", e);
                    }
                }

                if (data.status === 'inativo') {
                    setRole('apoio_inativo');
                } else {
                    setRole('apoio');
                }

                setPsychologistId(data.psychologistId);
                setPermissions(data.permissions || []);
                setUserName(data.name || 'Membro da Equipe');
                setPhotoURL(data.photoURL || null);
                setIsFirstAccess(!data.firstAccessCompleted);

                // Fetch psych info for expiration check
                if (data.psychologistId) {
                    try {
                        const pRef = doc(db, 'psicologos', data.psychologistId);
                        const pSnap = await getDocWithTimeout(pRef);
                        if (pSnap.exists()) {
                            const pData = pSnap.data();
                            currentPsychData = pData;
                            const pTrialEnd = pData.trialEndsAt ? (pData.trialEndsAt.toDate ? pData.trialEndsAt.toDate() : new Date(pData.trialEndsAt)) : null;
                            const pNextBillingDate = pData.nextBillingDate ? (pData.nextBillingDate.toDate ? pData.nextBillingDate.toDate() : new Date(pData.nextBillingDate)) : null;
                            const pIsBillingExpired = pNextBillingDate && new Date() > pNextBillingDate && pData.plan && pData.plan.toLowerCase() !== 'gratuito';
                            if ((pData.status === 'inativo' && pData.deactivatedBy !== 'user' && pData.deactivatedBy !== 'admin' && pTrialEnd && new Date() > pTrialEnd) || pData.subscriptionStatus === 'expirado' || pData.subscriptionStatus === 'overdue' || pData.status === 'inadimplente' || pIsBillingExpired) {
                                currentIsExpired = true;
                            }
                        }
                    } catch (pErr) {
                        logger.warn(`[AuthContext] Failed to fetch psychologist document for apoio user (might be rules propagation lag):`, pErr);
                    }
                }
                if (currentIsExpired) {
                    logger.warn(`[AuthContext] 🛑 Apoio: Professional account has expired or is inactive. Logging out.`);
                    sessionStorage.setItem('login_error_reason', 'psych_expired');
                    signOut(auth).catch(() => {});
                    clearRoleCache();
                    return;
                }
                setIsPlanExpired(false);
                setPsychData(currentPsychData);
                saveRoleCache({ uid, role: data.status === 'inativo' ? 'apoio_inativo' : 'apoio', psychologistId: data.psychologistId, userName: data.name || 'Membro da Equipe', photoURL: data.photoURL || null, permissions: data.permissions || [], isFirstAccess: !data.firstAccessCompleted, ts: Date.now() });
                return;
            }

            // MIGRATION / LEGACY APOIO CHECK (By Query)
            const apoioLegacyQuery = query(collection(db, 'apoio_users'), where('uid', '==', uid));
            const apoioLegacySnap = await getDocsWithTimeout(apoioLegacyQuery);

            if (!apoioLegacySnap.empty) {
                const legacyDoc = apoioLegacySnap.docs[0];
                const apoioData = legacyDoc.data();

                if (legacyDoc.id !== uid) {
                    logger.log(`[AuthContext] 🛠️ Migrating Apoio User from legacy ID (${legacyDoc.id}) to standard UID (${uid})...`);
                    try {
                        const batch = writeBatch(db);
                        // Create new doc with UID as Key
                        batch.set(doc(db, 'apoio_users', uid), {
                            ...apoioData,
                            uid: uid,
                            role: 'apoio', // Ensure role is set
                            migratedAt: new Date().toISOString()
                        });
                        // Delete old doc
                        batch.delete(legacyDoc.ref);
                        await batch.commit();
                        logger.log("[AuthContext] Migration Success.");
                    } catch (migErr) {
                        logger.error("[AuthContext] Migration Failed:", migErr);
                    }
                }

                if (apoioData.status === 'inativo') {
                    setRole('apoio_inativo');
                } else {
                    setRole('apoio');
                }
                setPsychologistId(apoioData.psychologistId);
                setPermissions(apoioData.permissions || []);
                setUserName(apoioData.name || 'Membro da Equipe');
                setPhotoURL(apoioData.photoURL || null);
                setIsFirstAccess(!apoioData.firstAccessCompleted);

                if (apoioData.psychologistId) {
                    try {
                        const pRef = doc(db, 'psicologos', apoioData.psychologistId);
                        const pSnap = await getDocWithTimeout(pRef);
                        if (pSnap.exists()) {
                            const pData = pSnap.data();
                            currentPsychData = pData;
                            const pTrialEnd = pData.trialEndsAt ? (pData.trialEndsAt.toDate ? pData.trialEndsAt.toDate() : new Date(pData.trialEndsAt)) : null;
                            const pNextBillingDate = pData.nextBillingDate ? (pData.nextBillingDate.toDate ? pData.nextBillingDate.toDate() : new Date(pData.nextBillingDate)) : null;
                            const pIsBillingExpired = pNextBillingDate && new Date() > pNextBillingDate && pData.plan && pData.plan.toLowerCase() !== 'gratuito';
                            if ((pData.status === 'inativo' && pData.deactivatedBy !== 'user' && pData.deactivatedBy !== 'admin' && pTrialEnd && new Date() > pTrialEnd) || pData.subscriptionStatus === 'expirado' || pData.subscriptionStatus === 'overdue' || pData.status === 'inadimplente' || pIsBillingExpired) {
                                currentIsExpired = true;
                            }
                        }
                    } catch (pErr) {
                        logger.warn(`[AuthContext] Failed to fetch psychologist document for legacy apoio user (might be rules propagation lag):`, pErr);
                    }
                }
                if (currentIsExpired) {
                    logger.warn(`[AuthContext] 🛑 Apoio Legacy: Professional account has expired or is inactive. Logging out.`);
                    sessionStorage.setItem('login_error_reason', 'psych_expired');
                    signOut(auth).catch(() => {});
                    clearRoleCache();
                    return;
                }
                setIsPlanExpired(false);
                setPsychData(currentPsychData);
                saveRoleCache({ uid, role: apoioData.status === 'inativo' ? 'apoio_inativo' : 'apoio', psychologistId: apoioData.psychologistId, userName: apoioData.name || 'Membro da Equipe', photoURL: apoioData.photoURL || null, permissions: apoioData.permissions || [], isFirstAccess: !apoioData.firstAccessCompleted, ts: Date.now() });
                return;
            }
            } // End of !isPatientEmail block

            // 3. Check Paciente (Document ID == Auth UID - New Standard)
            let patientDocRef = doc(db, 'pacientes', uid);
            let patientSnap = await getDocWithTimeout(patientDocRef);
            let pData: any = null;

            if (patientSnap.exists()) {
                pData = patientSnap.data();
            } else {
                // Fallback / Legacy query for patients not yet migrated to UID doc ID
                const patientQuery = query(collection(db, 'pacientes'), where('uid', '==', uid));
                const patientQuerySnap = await getDocsWithTimeout(patientQuery);
                if (!patientQuerySnap.empty) {
                    pData = patientQuerySnap.docs[0].data();
                }
            }

            if (pData) {
                // --- BLOQUEIO FINANCEIRO E CONTA EXCLUÍDA/INATIVA ---
                if (pData.accountStatus === 'blocked' || (pData.status === 'inativo' && pData.deactivatedBy !== 'user')) {
                    logger.warn(`[AuthContext] 🛑 Paciente bloqueado ou inativo detectado na inicialização (${uid}). Deslogando silenciosamente...`);
                    signOut(auth).catch(() => {});
                    setRole(null);
                    setPsychologistId(null);
                    setPermissions([]);
                    setUserName(null);
                    setIsFirstAccess(false);
                    return;
                }

                // --- REATIVAÇÃO SILENCIOSA (Background) ---
                if (pData.status === 'inativo' && pData.deactivatedBy === 'user') {
                    const scheduledDeletion = pData.scheduledDeletionAt ? (pData.scheduledDeletionAt.toDate ? pData.scheduledDeletionAt.toDate() : new Date(pData.scheduledDeletionAt)) : null;
                    if (scheduledDeletion && new Date() < scheduledDeletion) {
                        logger.log(`[AuthContext] ⚡ Reativação silenciosa detectada para Paciente ${uid}`);
                        // Encontra o doc correto (UID path ou query path)
                        const pRef = patientSnap.exists() ? patientDocRef : doc(db, 'pacientes', uid); 
                        // Nota: Se estiver no fallback, o doc id pode ser diferente, mas vamos tentar pelo UID
                        await updateDoc(pRef, {
                            status: 'ativo',
                            accountStatus: 'active',
                            deactivatedBy: deleteField(),
                            deactivatedAt: deleteField(),
                            scheduledDeletionAt: deleteField()
                        }).catch(() => {
                            // Fallback se o path UID falhar (doc id aleatório)
                            const q = query(collection(db, 'pacientes'), where('uid', '==', uid));
                            getDocs(q).then(s => {
                                if (!s.empty) updateDoc(s.docs[0].ref, { status: 'ativo', accountStatus: 'active', deactivatedBy: deleteField(), deactivatedAt: deleteField(), scheduledDeletionAt: deleteField() });
                            });
                        });
                        pData.status = 'ativo'; // Atualiza localmente
                    }
                }

                if (pData.status === 'inativo') {
                    setRole('paciente_inativo');
                } else {
                    setRole('paciente');
                }

                setPsychologistId(pData.psychologistId || null);
                setPermissions([]);
                setUserName(pData.nome || pData.name || 'Paciente');
                setPhotoURL(pData.photoURL || null);
                setIsFirstAccess(!pData.firstAccessCompleted);

                if (pData.psychologistId) {
                    try {
                        const pRef = doc(db, 'psicologos', pData.psychologistId);
                        const pSnap = await getDocWithTimeout(pRef);
                        if (pSnap.exists()) {
                            const psychDataSnapshot = pSnap.data();
                            currentPsychData = psychDataSnapshot;
                            const psychTrialEnd = psychDataSnapshot.trialEndsAt ? (psychDataSnapshot.trialEndsAt.toDate ? psychDataSnapshot.trialEndsAt.toDate() : new Date(psychDataSnapshot.trialEndsAt)) : null;
                            const psychNextBillingDate = psychDataSnapshot.nextBillingDate ? (psychDataSnapshot.nextBillingDate.toDate ? psychDataSnapshot.nextBillingDate.toDate() : new Date(psychDataSnapshot.nextBillingDate)) : null;
                            const psychIsBillingExpired = psychNextBillingDate && new Date() > psychNextBillingDate && psychDataSnapshot.plan && psychDataSnapshot.plan.toLowerCase() !== 'gratuito';
                            if ((psychDataSnapshot.status === 'inativo' && psychDataSnapshot.deactivatedBy !== 'user' && psychDataSnapshot.deactivatedBy !== 'admin' && psychTrialEnd && new Date() > psychTrialEnd) || psychDataSnapshot.subscriptionStatus === 'expirado' || psychDataSnapshot.subscriptionStatus === 'overdue' || psychDataSnapshot.status === 'inadimplente' || psychIsBillingExpired) {
                                currentIsExpired = true;
                            }
                        }
                    } catch (pErr) {
                        logger.warn(`[AuthContext] Failed to fetch psychologist document for patient (might be rules propagation lag):`, pErr);
                    }
                }
                if (currentIsExpired) {
                    logger.warn(`[AuthContext] 🛑 Patient: Professional account has expired or is inactive. Logging out.`);
                    sessionStorage.setItem('login_error_reason', 'psych_expired');
                    signOut(auth).catch(() => {});
                    clearRoleCache();
                    return;
                }
                setIsPlanExpired(false);
                setPsychData(currentPsychData);
                const resolvedRole = pData.status === 'inativo' ? 'paciente_inativo' : 'paciente';
                saveRoleCache({ uid, role: resolvedRole, psychologistId: pData.psychologistId || null, userName: pData.nome || pData.name || 'Paciente', photoURL: pData.photoURL || null, permissions: [], isFirstAccess: !pData.firstAccessCompleted, ts: Date.now() });
            } else {
                logger.warn("User authenticated but no role found in collections.");
                if (retries > 0) {
                    logger.log(`Retrying fetchUserRole... (${retries} left)`);
                    await new Promise(r => setTimeout(r, 1500));
                    return fetchUserRole(uid, firebaseUser, retries - 1);
                }
                setRole(null);
                setPsychologistId(null);
                setPermissions([]);
                setUserName(null);
                setIsFirstAccess(false);
                setIsPlanExpired(false);
                setPsychData(null);
            }

        } catch (error: any) {
            console.error(`[AuthContext] Error fetching user role (Retries left: ${retries}):`, error);
            logger.error(`Error fetching user role (Retries left: ${retries}):`, error);
            
            const errCode = error?.code;
            const errMsg = error?.message || '';
            const isPermissionError = errCode === 'permission-denied' || 
                                     errCode === 'unauthenticated' ||
                                     errMsg.includes('permission') || 
                                     errMsg.includes('insufficient');

            if (retries > 0 && !isPermissionError) {
                logger.log(`Network or Cache error. Retrying in 2s...`);
                await new Promise(r => setTimeout(r, 2000));
                return fetchUserRole(uid, firebaseUser, retries - 1);
            }
            setRole(null);
            setPermissions([]);
            setIsFirstAccess(false);
            setIsPlanExpired(false);
            setPsychData(null);
        }
    };

    // Session timeout configuration
    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    const WARNING_TIME = 25 * 60 * 1000; // 25 minutes (warn 5 min before)

    const logout = async () => {
        try {
            await logAudit({
                action: 'LOGOUT',
                userEmail: user?.email || '',
                role: role || 'user',
                userId: user?.uid || ''
            });
            clearRoleCache(); // Clear cached role on explicit logout
            await signOut(auth);
            setUser(null);
            setRole(null);
            setIsFirstAccess(false);
            setPsychologistId(null);
            setPermissions([]);
            setUserName(null);
            setIsPlanExpired(false);
            setPsychData(null);
        } catch (error) {
            logger.error('Logout error:', error);
        }
    };

    // Session timeout with inactivity detection
    useEffect(() => {
        // Aguarda ter o user E o role carregados antes de armar qualquer timer
        if (!user || !role) return; 

        // Detecção robusta de PWA/mobile/tablet
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
            || (window.navigator as any).standalone === true;
        
        const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const hasTouch = (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) || 'ontouchstart' in window;
        const isMobileWidth = window.innerWidth <= 1024;

        // Regra de negócio:
        // - NUNCA desloga Pacientes (eles precisam receber os push de lembrete do diário).
        // - NUNCA desloga se estiver usando PWA (isStandalone).
        // - NUNCA desloga em dispositivos mobile/tablet.
        if (role === 'paciente' || isStandalone || isMobileUserAgent || hasTouch || isMobileWidth) {
            logger.log(`[Auth] Auto-logout DESABILITADO (Mobile/PWA ou role=${role})`);
            return;
        }

        logger.log('[Auth] 🖥️ Desktop browser - Auto-logout ATIVO (30min)');

        let timeoutId: NodeJS.Timeout;
        let warningId: NodeJS.Timeout;

        const resetTimer = () => {
            clearTimeout(timeoutId);
            clearTimeout(warningId);

            warningId = setTimeout(() => {
                showToast('Sua sessão expirará em 5 minutos por inatividade', 'warning');
            }, WARNING_TIME);

            timeoutId = setTimeout(async () => {
                showToast('Sessão expirada por inatividade', 'info');
                await logout();
            }, INACTIVITY_TIMEOUT);
        };

        const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

        events.forEach(event => {
            window.addEventListener(event, resetTimer);
        });

        resetTimer();

        return () => {
            clearTimeout(timeoutId);
            clearTimeout(warningId);
            events.forEach(event => {
                window.removeEventListener(event, resetTimer);
            });
        };
    }, [user, role]);

    const initializeAuth = useCallback(() => {
        if (authInitializedRef.current) return;
        authInitializedRef.current = true;
        logger.log('[AuthContext] 🔄 Initializing onAuthStateChanged...');

        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            logger.log('[AuthContext] 🔔 onAuthStateChanged triggered!');

            setUser(firebaseUser);

            if (firebaseUser) {
                // Se estiver na rota de reset password, não tenta verificar role com Firestore
                // Ignoramos se o usuário já navegou para a tela de login (#/login)
                const isResetPasswordRoute = (window.location.hash.includes('reset-password') || 
                                             window.location.search.includes('resetPassword') || 
                                             window.location.search.includes('mode=resetPassword')) &&
                                             !window.location.hash.includes('login');
                if (isResetPasswordRoute) {
                    logger.log('[AuthContext] Reset password route active. Skipping role fetch and signing out.');
                    signOut(auth).catch(() => {});
                    setLoading(false);
                    return;
                }

                // ── INSTANT RESTORE: apply cached role immediately ──────────────
                // This makes the PWA reopen feel instant and prevents the redirect
                // to /login while Firestore is still loading.
                const cached = loadRoleCache(firebaseUser.uid);
                if (cached) {
                    logger.log(`[AuthContext] ⚡ Role restored from cache: ${cached.role}`);
                    setRole(cached.role as any);
                    setPsychologistId(cached.psychologistId);
                    setPermissions(cached.permissions);
                    setUserName(cached.userName);
                    setPhotoURL(cached.photoURL);
                    setIsFirstAccess(cached.isFirstAccess);
                    // Release loading immediately so PWALandingRouter can redirect
                    setLoading(false);
                }
                // ──────────────────────────────────────────────────────────────

                // Always verify with Firestore in background (updates cache too)
                logger.log('[AuthContext] 🔍 Verifying role with Firestore...');
                try {
                    await fetchUserRole(firebaseUser.uid, firebaseUser);
                    logger.log('[AuthContext] ✅ Role verified with Firestore');
                    
                    // Registra login na auditoria
                    logAudit({
                        action: 'LOGIN',
                        userId: firebaseUser.uid,
                        userEmail: firebaseUser.email || '',
                        role: 'pending_role', // O logAudit pegará os dados reais se não passarmos, mas aqui vamos tentar ser explícitos
                        details: { method: 'auto' }
                    });
                } catch (error) {
                    logger.error('[AuthContext] ❌ Error verifying role:', error);
                }
            } else {
                logger.log('[AuthContext] ⚠️ No user found - clearing state');
                clearRoleCache();
                setRole(null);
                setPsychologistId(null);
                setPermissions([]);
                setUserName(null);
                setPhotoURL(null);
                setIsFirstAccess(false);
                setIsPlanExpired(false);
                setPsychData(null);
            }

            setLoading(false);
            logger.log('[AuthContext] ✅ onAuthStateChanged complete, loading set to false');
        });

        unsubscribeRef.current = unsubscribe;
    }, []);

    // Effect to monitor hash changes to synchronize public/private route state
    useEffect(() => {
        const handleHashChange = () => {
            const currentHash = window.location.hash;
            const publicRoute = checkIsPublicRoute(currentHash);
            setIsPublicRoute(publicRoute);

            // If navigating to a private route or login, trigger auth initialization immediately
            if (!publicRoute) {
                initializeAuth();
            }
        };

        window.addEventListener('hashchange', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [initializeAuth]);

    // Initial auth trigger logic (deferred for public routes, immediate for private)
    // Wait until the persistence initialization is ready before registering state listeners
    useEffect(() => {
        if (!persistenceReady) return;

        let timeoutId: NodeJS.Timeout | null = null;

        if (!isPublicRoute) {
            initializeAuth();
        } else {
            logger.log('[AuthContext] Public route active. Deferring auth initialization by 1500ms...');
            timeoutId = setTimeout(() => {
                initializeAuth();
            }, 1500);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isPublicRoute, initializeAuth, persistenceReady]);

    // Cleanup effect to unsubscribe if component unmounts
    useEffect(() => {
        return () => {
            if (unsubscribeRef.current) {
                logger.log('[AuthContext] 🧹 Cleaning up onAuthStateChanged listener');
                unsubscribeRef.current();
            }
        };
    }, []);

    // NEW: Monitor user status changes (Force Logout if deactivated)
    useEffect(() => {
        if (!user || !role) return;

        // Se já está inativo no state (role *_inativo), não precisa monitorar para deslogar,
        // pois o login já tratou (ou a UI de reativação está ativa).
        if (role === 'apoio_inativo' || role === 'psicologo_inativo' || role === 'paciente_inativo') {
            return;
        }

        let unsubscribe = () => { };

        const setupStatusListener = async () => {
            try {
                if (role === 'apoio') {
                    const docRef = doc(db, 'apoio_users', user.uid);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        unsubscribe = onSnapshot(docRef, async (snap) => {
                            if (!snap.exists()) {
                                logger.log('[AuthContext] Apoio user doc deleted. Logging out.');
                                await logout();
                                return;
                            }
                            const data = snap.data();
                            
                            // ── Sincronização em Tempo Real (Self-Healing) ──
                            // Atualiza permissões e dados no state imediatamente se forem alterados
                            const currentRole = data.status === 'inativo' ? 'apoio_inativo' : 'apoio';
                            
                            setRole(currentRole as any);
                            setPermissions(data.permissions || []);
                            setUserName(data.name || data.nome || 'Membro da Equipe');
                            setPhotoURL(data.photoURL || null);
                            
                            // Atualiza o cache local para que refresh preserve os novos dados
                            saveRoleCache({
                                uid: user.uid,
                                role: currentRole,
                                psychologistId: data.psychologistId || null,
                                userName: data.name || data.nome || 'Membro da Equipe',
                                photoURL: data.photoURL || null,
                                permissions: data.permissions || [],
                                isFirstAccess: !data.firstAccessCompleted,
                                ts: Date.now()
                            });

                            // Só desloga se mudou para inativo enquanto estava ativo (role == apoio)
                            if (data.status === 'inativo' && role === 'apoio') {
                                logger.log('[AuthContext] Apoio user deactivated during session. Logging out.');
                                await logout();
                                showToast("Sua conta foi desativada.", "error");
                            }
                        });
                    }
                } else if (role === 'psicologo') {
                    const docRef = doc(db, 'psicologos', user.uid);
                    unsubscribe = onSnapshot(docRef, async (snap) => {
                        if (!snap.exists()) return;
                        const data = snap.data();
                        if (data.status === 'inativo') {
                            const isExpiration = data.deactivatedBy !== 'user' && data.deactivatedBy !== 'admin';
                            if (!isExpiration) {
                                logger.log('[AuthContext] Psychologist deactivated during session. Logging out.');
                                await logout();
                                showToast("Sua conta foi desativada.", "error");
                            }
                        }
                    });
                } else if (role === 'paciente') {
                    let docRef = doc(db, 'pacientes', user.uid);
                    const docSnap = await getDoc(docRef);
                    
                    if (!docSnap.exists()) {
                        const q = query(collection(db, 'pacientes'), where('uid', '==', user.uid));
                        const qSnap = await getDocs(q);
                        if (!qSnap.empty) {
                            docRef = qSnap.docs[0].ref;
                        }
                    }

                    unsubscribe = onSnapshot(docRef, async (snap) => {
                        if (!snap.exists()) {
                            logger.log('[AuthContext] Patient doc deleted. Logging out.');
                            await logout();
                            showToast("Sua conta foi excluída.", "error");
                            return;
                        }
                        const data = snap.data();
                        if (data.status === 'inativo') {
                            logger.log('[AuthContext] Patient deactivated during session. Logging out.');
                            await logout();
                            showToast("Sua conta foi desativada.", "error");
                        }
                    });
                }
            } catch (err) {
                logger.error("[AuthContext] Error setting up status listener", err);
            }
        };

        setupStatusListener();

        return () => {
            unsubscribe();
        };
    }, [user, role]);

    const refreshRole = async () => {
        if (user) {
            await fetchUserRole(user.uid);
        }
    };

    const value = {
        user,
        loading,
        role,
        isFirstAccess,
        isPlanExpired,
        psychData,
        psychologistId,
        permissions,
        userName,
        photoURL,
        logout,
        refreshRole
    };

    const shouldShowLoading = (loading || !persistenceReady) && !isPublicRoute;

    return (
        <AuthContext.Provider value={value}>
            {shouldShowLoading ? (
                <div className="flex items-center justify-center min-h-screen bg-[#0B1121]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
                </div>
            ) : children}
        </AuthContext.Provider>
    );
};
